import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, HardDrive } from "lucide-react";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { EmptyState } from "../shared/EmptyState";
import { PageLoader } from "../shared/LoadingSpinner";
import { toast } from "sonner";

const RESOURCE_TYPES = [
    { id: "network_share", label: "Partage réseau" },
    { id: "server", label: "Serveur" },
    { id: "application", label: "Application" },
    { id: "physical", label: "Accès physique" },
];

export function AdminResources() {
    const { departments, users } = useApp();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        resource_type: "network_share",
        unc_path: "",
        department_owner_id: "",
        manager_id: "",
    });

    useEffect(() => {
        loadResources();
    }, []);

    const loadResources = async () => {
        try {
            setLoading(true);
            const data = await api.getResourcesCatalog();
            setResources(data);
        } catch (error) {
            toast.error("Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditing(item);
            setFormData({
                name: item.name,
                resource_type: item.resource_type,
                unc_path: item.unc_path || "",
                department_owner_id: item.department_owner_id || "",
                manager_id: item.manager_id || "",
            });
        } else {
            setEditing(null);
            setFormData({ name: "", resource_type: "network_share", unc_path: "", department_owner_id: "", manager_id: "" });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editing) {
                await api.updateResource(editing.id, formData);
                toast.success("Ressource mise à jour");
            } else {
                await api.createResource(formData);
                toast.success("Ressource créée");
            }
            setDialogOpen(false);
            loadResources();
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette ressource ?")) return;
        try {
            await api.deleteResource(id);
            toast.success("Ressource supprimée");
            loadResources();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    const getResourceTypeLabel = (typeId) => {
        return RESOURCE_TYPES.find((t) => t.id === typeId)?.label || typeId;
    };

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="admin-resources">
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Catalogue ressources</h1>
                    <p className="page-subtitle">{resources.length} ressources</p>
                </div>
                <Button onClick={() => handleOpenDialog()} data-testid="add-resource-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                </Button>
            </div>

            {resources.length === 0 ? (
                <div className="card">
                    <EmptyState
                        icon={HardDrive}
                        title="Aucune ressource"
                        description="Ajoutez des ressources au catalogue"
                    />
                </div>
            ) : (
                <div className="card">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Type</th>
                                    <th>Chemin UNC</th>
                                    <th>Département</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {resources.map((res) => {
                                    const dept = departments.find((d) => d.id === res.department_owner_id);
                                    return (
                                        <tr key={res.id} data-testid={`resource-row-${res.id}`}>
                                            <td className="font-medium">{res.name}</td>
                                            <td>{getResourceTypeLabel(res.resource_type)}</td>
                                            <td className="font-mono text-sm text-gray-500">
                                                {res.unc_path || "—"}
                                            </td>
                                            <td>{dept?.name || "—"}</td>
                                            <td>
                                                <div className="flex items-center gap-2 justify-end">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleOpenDialog(res)}
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDelete(res.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-500" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? "Modifier la ressource" : "Ajouter une ressource"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="form-label">Nom *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ex: Partage Commun"
                                data-testid="resource-name"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Type</Label>
                            <Select
                                value={formData.resource_type}
                                onValueChange={(v) => setFormData({ ...formData, resource_type: v })}
                            >
                                <SelectTrigger data-testid="resource-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {RESOURCE_TYPES.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="form-label">Chemin UNC</Label>
                            <Input
                                value={formData.unc_path}
                                onChange={(e) => setFormData({ ...formData, unc_path: e.target.value })}
                                placeholder="Ex: \\\\serveur\\partage"
                                data-testid="resource-unc"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Département propriétaire</Label>
                            <Select
                                value={formData.department_owner_id}
                                onValueChange={(v) => setFormData({ ...formData, department_owner_id: v })}
                            >
                                <SelectTrigger data-testid="resource-dept">
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {departments.map((d) => (
                                        <SelectItem key={d.id} value={d.id}>
                                            {d.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="form-label">Responsable</Label>
                            <Select
                                value={formData.manager_id}
                                onValueChange={(v) => setFormData({ ...formData, manager_id: v })}
                            >
                                <SelectTrigger data-testid="resource-manager">
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSave} disabled={!formData.name} data-testid="save-resource-btn">
                            {editing ? "Enregistrer" : "Créer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
